// Capability gating for the block-restructure edits (P3 #21): given a process
// WalkNode, decide whether it can be UNWRAPPED (flattened in place) and which
// constructs it can cleanly MORPH into. Pure over the walk tree so every surface
// (tree spine, future flow/BPMN affordances) offers the SAME items — and so the
// gate matches the engine's `unwrapBlock` / `morphBlock` refusals exactly,
// keeping the affordance from being offered when it would only refuse.
//
// Lives outside StepEditMenu.tsx (a component file) so it can be imported without
// a Fast-Refresh incompatible export, and unit-tested directly.

import type { WalkNode } from "@archlang/engine";
import type { WrapConstruct } from "./processStepEdit.js";

/** The block-restructure capabilities of one walk node. */
export interface BlockMorphCaps {
  /** The block can be flattened in place (engine `unwrapBlock` would apply). */
  readonly canUnwrap: boolean;
  /** Constructs the block can cleanly morph INTO (engine `morphBlock` targets). */
  readonly morphTargets: readonly WrapConstruct[];
}

const NONE: BlockMorphCaps = { canUnwrap: false, morphTargets: [] };

/**
 * A `parallel` maps onto the single-body constructs (unwrap / morph-to-`each`)
 * ONLY when it carries a single body — a lone (implicit) branch or a fan-out.
 * The walk exposes only the branch COUNT, not whether that lone branch was an
 * implicit body or an explicit `branch { … }` (both render as one `WalkBranch`
 * with a synthetic `branch:0` label). The precise implicit-vs-explicit call
 * needs the IR, which the walk erases — so this coarse gate offers the affordance
 * for any single-body parallel, and the ENGINE is the exact guard: it refuses a
 * single EXPLICIT-branch parallel (`compound-step`), surfacing the reason through
 * the shared onReject toast. A `mergeAspect` (join/race/out/await threshold) also
 * blocks the morph — a loop has no merge analog — but not the unwrap of a plain
 * single body.
 */
function parallelHasSimpleBody(node: Extract<WalkNode, { kind: "parallel" }>): boolean {
  return node.branches.length <= 1;
}

/** Compute the unwrap/morph capabilities of a walk node. Non-block / lossy nodes
 *  get {@link NONE} (no affordance offered). */
export function blockMorphCaps(node: WalkNode): BlockMorphCaps {
  switch (node.kind) {
    case "each":
      // A plain loop flattens (head dropped) and morphs to a parallel.
      return { canUnwrap: true, morphTargets: node.retry ? [] : ["parallel"] };
    case "parallel": {
      const simple = parallelHasSimpleBody(node);
      // A bare single body unwraps; morph-to-each additionally needs no merge/await
      // clause (a threshold has no loop analog).
      const canMorph = simple && !node.mergeAspect;
      return { canUnwrap: simple, morphTargets: canMorph ? ["each"] : [] };
    }
    case "if":
      // A single-arm `if` with no `else` flattens (its condition is dropped); an
      // `if`+`else`/`else if` would lose arms → refused.
      return { canUnwrap: node.branches.length === 1 && !node.elseBranch, morphTargets: [] };
    case "try":
      // A `try` with no `catch` flattens; catches would be lost → refused.
      return { canUnwrap: node.catches.length === 0, morphTargets: [] };
    default:
      // `switch` (select cases aren't a flat flow), `do`, steps, terminators.
      return NONE;
  }
}
